import axios from "axios";
import { useEffect, useState } from "react";
import DatePicker from "react-datepicker";
import "react-datepicker/dist/react-datepicker.css";
import { AiOutlineDelete, AiOutlineEdit } from "react-icons/ai";
import { FaLocationDot } from "react-icons/fa6";
import { useSelector } from "react-redux";
import LoaderSpiner from "../../../Components/LoaderSpiner";
import styles from "./CreateProject.module.css";
import { MdDashboard } from "react-icons/md";

const typeOptions = ["Residential", "Commercial", "Villas", "Other"];

const ShowAllProjectCards = ({
  projects,
  developerMap,
  role,
  handleEdit,
  handleDelete,
  setViewProject,
  setShowViewModal,
  setShowCreateModal,
}) => {
  const groupProjectsByDeveloper = (projects) => {
    const grouped = {};
    projects.forEach((project) => {
      const developerName = project.developerId?.name || "Unknown Developer";
      if (!grouped[developerName]) grouped[developerName] = [];
      grouped[developerName].push(project);
    });
    return grouped;
  };

  return (
    <div className={styles.projectListContainer}>
      <div className={styles.searchContainer}>
        <input
          type="text"
          placeholder="Search projects..."
          className={styles.searchBar}
          aria-label="Search projects"
        />
        {role === "vendor" && (
          <button
            className={styles.button}
            onClick={() => setShowCreateModal(true)}
            aria-label="Add New Project"
          >
            + Add New Project
          </button>
        )}
      </div>
      <div className={styles.projectCardContainer}>
        {Object.entries(groupProjectsByDeveloper(projects)).map(
          ([developer, projectsList]) => (
            <div key={developer} className={styles.developerSection}>
              {role === "vendor" && (
                <h6 className={styles.developerName}>{developer}</h6>
              )}
              {projectsList.length === 0 ? (
                <p className={styles.message}>
                 <MdDashboard />
                </p>
              ) : (
                <div className={styles.projectCards}>
                  {projectsList.map((project) => (
                    <div
                      key={project._id}
                      className={styles.projectCard}
                      role="button"
                      onClick={() => {
                        setViewProject(project);
                        setShowViewModal(true);
                      }}
                      tabIndex={0}
                      onKeyDown={(e) => {
                        if (e.key === "Enter") {
                          setViewProject(project);
                          setShowViewModal(true);
                        }
                      }}
                    >
                      <div className={styles.projectCardHeader}>
                        <h3 className={styles.projectTitle}>{project.projectname}</h3>
                        {role === "vendor" && (
                          <div className={styles.iconContainer}>
                            <AiOutlineEdit
                              size={18}
                              className={styles.icon}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleEdit(project);
                              }}
                              aria-label="Edit project"
                            />
                            <AiOutlineDelete
                              size={18}
                              className={`${styles.icon} ${styles.deleteIcon}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDelete(project._id);
                              }}
                              aria-label="Delete project"
                            />
                          </div>
                        )}
                      </div>
                      <div className={styles.projectContent}>
                        {project.location && (
                          <p className={styles.projectDetail}>
                            <FaLocationDot className={styles.iconSmall} aria-label="location" />{" "}
                            {project.location}
                          </p>
                        )}
                        {project.type && (
                          <p className={styles.projectDetail}>
                            {project.type}{" "}
                            {project.totalUnits && `(${project.totalUnits} Units)`}
                          </p>
                        )}
                        {(project.launchDate || project.completionDate) && (
                          <p className={styles.projectDetail}>
                            {project.launchDate?.split("T")[0] || "-"} -{" "}
                            {project.completionDate?.split("T")[0] || "-"}
                          </p>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )
        )}
      </div>
    </div>
  );
};

const ProjectCreationPage = ({
  form,
  setForm,
  developers,
  handleSubmit,
  handleChange,
  handleLaunchDateChange,
  handleCompletionDateChange,
  loading,
  message,
  setShowCreateModal,
}) => {
  return (
    <div className={styles.modalContent}>
      <button
        className={styles.closeButton}
        onClick={() => setShowCreateModal(false)}
        aria-label="Close modal"
      >
        Cancel
      </button>
      <h2 className={styles.heading}>
        {form._id ? "Edit Project" : "Create Project"}
      </h2>
      <form className={styles.form} onSubmit={handleSubmit}>
        <div className={`${styles.formGrid} ${styles.section}`}>
          <h3 className={styles.sectionHeading}>Project Details</h3>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>
              Project Name <span className={styles.required}>*</span>
            </label>
            <input
              className={styles.input}
              name="projectname"
              placeholder="Enter Project Name"
              value={form.projectname}
              onChange={(e) => {
                const value = e.target.value;
                if (/^[A-Za-z ]*$/.test(value)) {
                  handleChange(e);
                }
              }}
              title="Project name should only contain letters and spaces"
              required
              aria-label="Project Name"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>
              Project Description <span className={styles.required}>*</span>
            </label>
            <input
              className={styles.input}
              name="projectdescription"
              placeholder="Enter Project Description"
              value={form.projectdescription}
              onChange={handleChange}
              required
              aria-label="Project Description"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>
              Developer <span className={styles.required}>*</span>
            </label>
            <select
              className={styles.select}
              name="developerId"
              value={form.developerId}
              onChange={handleChange}
              required
              aria-label="Select Developer"
            >
              <option value="">Select Developer</option>
              {developers.map((dev) => (
                <option key={dev._id} value={dev._id}>
                  {dev.name}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Location/City</label>
            <input
              className={styles.input}
              name="location"
              placeholder="Enter Location/City"
              value={form.location}
              onChange={handleChange}
              aria-label="Location"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Project Type</label>
            <select
              className={styles.select}
              name="type"
              value={form.type}
              onChange={handleChange}
              aria-label="Project Type"
            >
              <option value="">Select Type</option>
              {typeOptions.map((type) => (
                <option key={type} value={type}>
                  {type}
                </option>
              ))}
            </select>
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Land Parcel (in acres)</label>
            <input
              className={styles.input}
              name="landParcel"
              placeholder="Enter Land Parcel (in acres)"
              value={form.landParcel}
              onChange={handleChange}
              aria-label="Land Parcel"
            />
          </div>
        </div>
        <div className={`${styles.formGrid} ${styles.section}`}>
          <h3 className={styles.sectionHeading}>Project Specifications</h3>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Total Towers</label>
            <input
              className={styles.input}
              name="totalTowers"
              type="number"
              min="1"
              placeholder="Enter Total Towers"
              value={form.totalTowers}
              onChange={handleChange}
              aria-label="Total Towers"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Launching Towers</label>
            <input
              className={styles.input}
              name="launchingTowers"
              type="number"
              min="1"
              max={form.totalTowers}
              placeholder="Enter Launching Towers"
              value={form.launchingTowers}
              onChange={handleChange}
              aria-label="Launching Towers"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Total Units</label>
            <input
              className={styles.input}
              name="totalUnits"
              type="number"
              min="1"
              placeholder="Enter Total Units"
              value={form.totalUnits}
              onChange={handleChange}
              aria-label="Total Units"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Per Floor Flats</label>
            <input
              className={styles.input}
              name="perFloorFlats"
              type="number"
              min="1"
              placeholder="Enter Per Floor Flats"
              value={form.perFloorFlats}
              onChange={handleChange}
              aria-label="Per Floor Flats"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Total Lifts</label>
            <input
              className={styles.input}
              name="totalLifts"
              type="number"
              min="1"
              placeholder="Enter Total Lifts"
              value={form.totalLifts}
              onChange={handleChange}
              aria-label="Total Lifts"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Floors</label>
            <input
              className={styles.input}
              name="floors"
              placeholder="e.g., Basement+LG+UG+M+3P+26Floor"
              value={form.floors}
              onChange={handleChange}
              aria-label="Floors"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Launch Date</label>
            <DatePicker
              className={styles.dateInput}
              selected={form.launchDate}
              onChange={handleLaunchDateChange}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select Launch Date"
              aria-label="Launch Date"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Possession Date</label>
            <DatePicker
              className={styles.dateInput}
              selected={form.completionDate}
              onChange={handleCompletionDateChange}
              dateFormat="yyyy-MM-dd"
              placeholderText="Select Possession Date"
              aria-label="Possession Date"
            />
          </div>
        </div>
        <div className={`${styles.formGrid} ${styles.section}`}>
          <h3 className={styles.sectionHeading}>Additional Information</h3>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Configuration and Pricing</label>
            <textarea
              className={styles.textarea}
              name="configurationPricing"
              placeholder="e.g., 2BHK: 654 sqft - INR 74-78 lacs"
              value={form.configurationPricing}
              onChange={handleChange}
              aria-label="Configuration and Pricing"
            />
          </div>
          <div className={styles.inputWrapper}>
            <label className={styles.label}>Unique Selling Points</label>
            <textarea
              className={styles.textarea}
              name="usp"
              placeholder="e.g., First 300 meter connected skywalk"
              value={form.usp}
              onChange={handleChange}
              aria-label="Unique Selling Points"
            />
          </div>
        </div>
        <div className={styles.formActions}>
          <button
            className={styles.submitButton}
            type="submit"
            disabled={loading}
            aria-label={form._id ? "Update Project" : "Create Project"}
          >
            {loading
              ? "Processing..."
              : form._id
                ? "Update Project"
                : "Create Project"}
          </button>
          <button
            className={styles.cancelButton}
            type="button"
            onClick={() => setShowCreateModal(false)}
            aria-label="Cancel"
          >
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

// Rest of the component remains the same...
const CreateProject = () => {
  // ... existing state and logic remains unchanged
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [form, setForm] = useState({
    projectname: "",
    projectdescription: "",
    developerId: "",
    location: "",
    type: "",
    landParcel: "",
    totalTowers: "",
    launchingTowers: "",
    totalUnits: "",
    perFloorFlats: "",
    totalLifts: "",
    floors: "",
    configurationPricing: `2BHK: 654 sqft - INR 74-78 lacs
2BHK: 734 sqft - INR 83-87 lacs
3BHK: 972 sqft - INR 1.10-1.14 cr
3BHK: 1082 sqft - INR 1.22-1.26 cr`,
    usp: `First 300 meter connected skywalk, 100 m above the ground
First multi celebration zones
First personal pickleball court
Move under the stars at a height of 100 meter
Flexi-me-space
Personal high street
First cabana lounge`,
    launchDate: null,
    completionDate: new Date("2030-06-01"),
  });
  const [developers, setDevelopers] = useState([]);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [editProjectId, setEditProjectId] = useState(null);
  const [projects, setProjects] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [viewProject, setViewProject] = useState(null);
  const token = useSelector((state) => state.auth.token);
  const role = localStorage.getItem("role");
  const userId = localStorage.getItem("userId");
  const [developerMap, setDeveloperMap] = useState({});

  // ... all useEffect, handlers remain the same
  useEffect(() => {
    const fetchDevelopers = async () => {
      try {
        if (!token) {
          setMessage("Authentication token missing. Please log in again.");
          return;
        }
        setLoading(true);
        const res = await axios.get(
          "https://prod.toxboxai.com/api/developer/getalldevelopers",
          {
            headers: { Authorization: `Bearer ${token}` },
          }
        );
        if (res.data.success && Array.isArray(res.data.data.developers)) {
          setDevelopers(res.data.data.developers);
          const devMap = {};
          res.data.data.developers.forEach((dev) => {
            devMap[dev._id] = dev.name;
          });
          setDeveloperMap(devMap);
        } else {
          setMessage("Failed to load developers: Invalid response format");
        }
      } catch (err) {
        setMessage(`Error fetching developers: ${err.message}`);
        console.error("Fetch Developers Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchDevelopers();
  }, [token]);

  useEffect(() => {
    const fetchProjects = async () => {
      try {
        if (!token) {
          setMessage("Authentication token missing. Please log in again.");
          return;
        }
        if (!userId) {
          setMessage("User ID not found. Please log in again.");
          return;
        }
        setLoading(true);
        let url = "";
        if (role === "participant") {
          url = "https://prod.toxboxai.com/api/campaign/assigned-projects";
        } else if (role === "vendor") {
          url = "https://prod.toxboxai.com/api/project/getproject";
        } else {
          setMessage("Invalid role detected");
          setLoading(false);
          return;
        }

        const res = await axios.get(url, {
          headers: { Authorization: `Bearer ${token}` },
        });

        if (res.data.success && Array.isArray(res.data.data.projects)) {
          const updatedProjects = res.data.data.projects.map((project) => ({
            ...project,
            developerId:
              typeof project.developerId === "string"
                ? {
                  _id: project.developerId,
                  name:
                    developerMap[project.developerId] || "Unknown Developer",
                }
                : project.developerId || { name: "Unknown Developer" },
          }));
          setProjects(updatedProjects);
          if (updatedProjects.length === 0) {
            setMessage("No projects found for this vendor.");
          }
        } else {
          setMessage("Failed to load projects: Invalid response format");
        }
      } catch (err) {
        setMessage(
          `Error fetching projects: ${err.response?.data?.error || err.message}`
        );
        console.error("Fetch Projects Error:", err);
      } finally {
        setLoading(false);
      }
    };
    fetchProjects();
  }, [token, refresh, role, developerMap, userId]);

  // ... all other handlers remain the same (handleChange, handleDelete, handleEdit, etc.)
  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm({ ...form, [name]: value });
  };

  const handleDelete = async (projectId) => {
    const confirmDelete = window.confirm(
      "Are you sure you want to delete this project?"
    );
    if (!confirmDelete) return;

    try {
      setLoading(true);
      const res = await axios.delete(
        `https://prod.toxboxai.com/api/project/delete/${projectId}`,
        {
          headers: { Authorization: `Bearer ${token}` },
        }
      );

      if (res.data.success) {
        setMessage("Project deleted successfully!");
        setRefresh((prev) => !prev);
      } else {
        setMessage(res.data.error || "Failed to delete project.");
      }
    } catch (error) {
      setMessage(
        `Error deleting project: ${error.response?.data?.error || error.message
        }`
      );
      console.error("Delete Project Error:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (project) => {
    setForm({
      projectname: project.projectname || "",
      projectdescription: project.projectdescription || "",
      developerId: project.developerId?._id || project.developerId || "",
      location: project.location || "",
      type: project.type || "",
      landParcel: project.landParcel || "",
      totalTowers: project.totalTowers || "",
      launchingTowers: project.launchingTowers || "",
      totalUnits: project.totalUnits || "",
      perFloorFlats: project.perFloorFlats || "",
      totalLifts: project.totalLifts || "",
      floors: project.floors || "",
      configurationPricing: Array.isArray(project.configurationPricing)
        ? project.configurationPricing.join("\n")
        : project.configurationPricing || "",
      usp: Array.isArray(project.usp)
        ? project.usp.join("\n")
        : project.usp || "",
      launchDate: project.launchDate ? new Date(project.launchDate) : null,
      completionDate: project.completionDate
        ? new Date(project.completionDate)
        : null,
      _id: project._id,
    });
    setEditProjectId(project._id);
    setShowCreateModal(true);
  };

  const handleLaunchDateChange = (date) => {
    setForm((prev) => ({ ...prev, launchDate: date }));
  };

  const handleCompletionDateChange = (date) => {
    setForm((prev) => ({ ...prev, completionDate: date }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setMessage("");

    if (!userId) {
      setMessage("User ID not found. Please log in again.");
      return;
    }
    if (!token) {
      setMessage("Authentication token missing. Please log in again.");
      return;
    }
    if (!form.projectname || !form.projectdescription || !form.developerId) {
      setMessage("Project name, description, and developer are required");
      return;
    }
    if (Number(form.launchingTowers) > Number(form.totalTowers)) {
      setMessage("Launching towers cannot exceed total towers");
      return;
    }

    try {
      setLoading(true);
      const payload = {
        projectname: form.projectname,
        projectdescription: form.projectdescription,
        developerId: form.developerId,
        vendorId: userId,
        location: form.location || undefined,
        type: form.type || undefined,
        landParcel: form.landParcel ? Number(form.landParcel) : undefined,
        totalTowers: form.totalTowers ? Number(form.totalTowers) : undefined,
        launchingTowers: form.launchingTowers
          ? Number(form.launchingTowers)
          : undefined,
        totalUnits: form.totalUnits ? Number(form.totalUnits) : undefined,
        perFloorFlats: form.perFloorFlats
          ? Number(form.perFloorFlats)
          : undefined,
        totalLifts: form.totalLifts ? Number(form.totalLifts) : undefined,
        floors: form.floors || undefined,
        configurationPricing: form.configurationPricing || undefined,
        usp: form.usp || undefined,
        launchDate: form.launchDate
          ? form.launchDate.toISOString().split("T")[0]
          : undefined,
        completionDate: form.completionDate
          ? form.completionDate.toISOString().split("T")[0]
          : undefined,
      };

      const url = editProjectId
        ? `https://prod.toxboxai.com/api/project/update/${editProjectId}`
        : "https://prod.toxboxai.com/api/project/createProject";
      const method = editProjectId ? axios.put : axios.post;

      const res = await method(url, payload, {
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.data.success) {
        setMessage(
          editProjectId
            ? "Project updated successfully!"
            : "Project created successfully!"
        );
        setForm({
          projectname: "",
          projectdescription: "",
          developerId: "",
          location: "",
          type: "",
          landParcel: "",
          totalTowers: "",
          launchingTowers: "",
          totalUnits: "",
          perFloorFlats: "",
          totalLifts: "",
          floors: "",
          configurationPricing: `2BHK: 654 sqft - INR 74-78 lacs
2BHK: 734 sqft - INR 83-87 lacs
3BHK: 972 sqft - INR 1.10-1.14 cr
3BHK: 1082 sqft - INR 1.22-1.26 cr`,
          usp: `First 300 meter connected skywalk, 100 m above the ground
First multi celebration zones
First personal pickleball court
Move under the stars at a height of 100 meter
Flexi-me-space
Personal high street
First cabana lounge`,
          launchDate: null,
          completionDate: new Date("2030-06-01"),
        });
        setEditProjectId(null);
        setShowCreateModal(false);
        setRefresh((r) => !r);
      } else {
        setMessage(
          res.data.error ||
          `Error ${editProjectId ? "updating" : "creating"} project`
        );
      }
    } catch (err) {
      setMessage(
        `Failed to ${editProjectId ? "update" : "create"} project: ${err.response?.data?.error || err.message
        }`
      );
      console.error("Project Error:", err);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {loading ? (
        <LoaderSpiner />
      ) : (
        <div className={styles.container}>
          <ShowAllProjectCards
            projects={projects}
            developerMap={developerMap}
            role={role}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            setViewProject={setViewProject}
            setShowViewModal={setShowViewModal}
            setShowCreateModal={setShowCreateModal}
          />
          {showCreateModal && (
            <div className={styles.modalBackdrop}>
              <ProjectCreationPage
                form={form}
                setForm={setForm}
                developers={developers}
                handleSubmit={handleSubmit}
                handleChange={handleChange}
                handleLaunchDateChange={handleLaunchDateChange}
                handleCompletionDateChange={handleCompletionDateChange}
                loading={loading}
                message={message}
                setShowCreateModal={setShowCreateModal}
              />
            </div>
          )}
          {showViewModal && viewProject && (
            <div className={styles.modalBackdrop}>
              <div className={styles.modalContent}>
                <button
                  className={styles.closeButton}
                  onClick={() => setShowViewModal(false)}
                  aria-label="Close modal"
                >
                  Back
                </button>
                <h3 className={styles.heading}>Project Details</h3>
                <div className={styles.projectDetailsContainer}>
                  <table className={styles.detailsTable}>
                    <tbody>
                      {viewProject.projectname && (
                        <tr>
                          <td>
                            <strong>Project Name:</strong>
                          </td>
                          <td>{viewProject.projectname}</td>
                        </tr>
                      )}
                      {viewProject.projectdescription && (
                        <tr>
                          <td>
                            <strong>Description:</strong>
                          </td>
                          <td>{viewProject.projectdescription}</td>
                        </tr>
                      )}
                      {viewProject.developerId?.name && (
                        <tr>
                          <td>
                            <strong>Developer:</strong>
                          </td>
                          <td>{viewProject.developerId.name}</td>
                        </tr>
                      )}
                      {viewProject.location && (
                        <tr>
                          <td>
                            <strong>Location:</strong>
                          </td>
                          <td>{viewProject.location}</td>
                        </tr>
                      )}
                      {viewProject.type && (
                        <tr>
                          <td>
                            <strong>Type:</strong>
                          </td>
                          <td>{viewProject.type}</td>
                        </tr>
                      )}
                      {viewProject.landParcel && (
                        <tr>
                          <td>
                            <strong>Land Parcel:</strong>
                          </td>
                          <td>{viewProject.landParcel}</td>
                        </tr>
                      )}
                      {viewProject.totalTowers && (
                        <tr>
                          <td>
                            <strong>Total Towers:</strong>
                          </td>
                          <td>{viewProject.totalTowers}</td>
                        </tr>
                      )}
                      {viewProject.launchingTowers && (
                        <tr>
                          <td>
                            <strong>Launching Towers:</strong>
                          </td>
                          <td>{viewProject.launchingTowers}</td>
                        </tr>
                      )}
                      {viewProject.totalUnits && (
                        <tr>
                          <td>
                            <strong>Total Units:</strong>
                          </td>
                          <td>{viewProject.totalUnits}</td>
                        </tr>
                      )}
                      {viewProject.perFloorFlats && (
                        <tr>
                          <td>
                            <strong>Per Floor Flats:</strong>
                          </td>
                          <td>{viewProject.perFloorFlats}</td>
                        </tr>
                      )}
                      {viewProject.totalLifts && (
                        <tr>
                          <td>
                            <strong>Total Lifts:</strong>
                          </td>
                          <td>{viewProject.totalLifts}</td>
                        </tr>
                      )}
                      {viewProject.floors && (
                        <tr>
                          <td>
                            <strong>Floors:</strong>
                          </td>
                          <td>{viewProject.floors}</td>
                        </tr>
                      )}
                      {viewProject.configurationPricing && (
                        <tr>
                          <td>
                            <strong>Configuration & Pricing:</strong>
                          </td>
                          <td>
                            {Array.isArray(viewProject.configurationPricing)
                              ? viewProject.configurationPricing.join("\n")
                              : viewProject.configurationPricing}
                          </td>
                        </tr>
                      )}
                      {viewProject.usp && (
                        <tr>
                          <td>
                            <strong>Unique Selling Points:</strong>
                          </td>
                          <td>
                            {Array.isArray(viewProject.usp)
                              ? viewProject.usp.join("\n")
                              : viewProject.usp}
                          </td>
                        </tr>
                      )}
                      {viewProject.launchDate && (
                        <tr>
                          <td>
                            <strong>Launch Date:</strong>
                          </td>
                          <td>{viewProject.launchDate.split("T")[0]}</td>
                        </tr>
                      )}
                      {viewProject.completionDate && (
                        <tr>
                          <td>
                            <strong>Possession Date:</strong>
                          </td>
                          <td>{viewProject.completionDate.split("T")[0]}</td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}

     
        </div>
      )}
    </>
  );
};

export default CreateProject;